/**
 * Shared design tokens for the hierarchical PAMM offers tree.
 *
 * The reference design is a Tailwind prototype; these constants translate its palette
 * into plain values usable by Material-UI `makeStyles` / emotion in this repo.
 */
export const COLORS = {
  brand: '#007AFF',
  success: '#1DCD9A',
  // Negative PnL only. Distinct from MASTER_LEVEL_COLOR (a level accent), so a loss never reads
  // as a level badge; deeper than the accent red for legibility on small KPI numbers.
  danger: '#DC2626',
  foreground: '#1F2937',
  mutedText: '#6B7280',
  border: '#E5E7EB',
  cardBg: '#FFFFFF',
  mutedBg: '#F8FAFC',
  infoBg: '#EFF6FF',
  infoBorder: '#BFDBFE',
  infoText: '#1E3A8A',
}

/**
 * Accent colour of the master Money Manager offer (level 0). Red is reserved
 * exclusively for the master — no agent level ever uses it.
 */
export const MASTER_LEVEL_COLOR = '#EF4444'

/**
 * Repeating accent palette for agent levels, ordered level 1 → level 4. Level 4 is
 * green; level 5 and deeper restart the cycle (level 5 reuses level 1's colour, and
 * so on), so the tree stays readable at unlimited depth.
 */
export const AGENT_LEVEL_COLORS = ['#007AFF', '#7C3AED', '#F59E0B', '#1DCD9A']

/** Agent levels shown in the colour legend before the {@link AGENT_LEVEL_COLORS} cycle repeats. */
export const LEGEND_AGENT_LEVELS = [1, 2, 3, 4]

/**
 * Returns the accent colour for a given tree depth.
 *
 * Level 0 (the master Money Manager offer) is always {@link MASTER_LEVEL_COLOR}. Agent
 * levels (1+) cycle through {@link AGENT_LEVEL_COLORS} with a period of four, so level 4
 * is green and level 5 onward repeats the sequence starting from level 1's colour.
 */
export const getLevelColor = (level: number): string => {
  if (level <= 0) return MASTER_LEVEL_COLOR
  return AGENT_LEVEL_COLORS[(level - 1) % AGENT_LEVEL_COLORS.length]
}
